<script lang="ts">
  import SignedThumbnailImage from '../lib/components/SignedThumbnailImage.svelte';
  import { navigate, useLocation } from 'svelte-routing';
  import { _ } from 'svelte-i18n';
  import { api, type Followup } from '../lib/api';
  import { userSettings } from '../lib/stores/settingsStore';
  import { toastStore } from '../lib/stores/toastStore';
  import { formatDate } from '../lib/settings';
  import { followupsStore } from '../lib/stores/followupsStore';
  import { groupFollowups, isFollowupOverdue, followupErrorMessage } from '../lib/followups';
  import PageHeader from '../lib/components/layout/PageHeader.svelte';
  import { Button, Spinner, EmptyState, Badge } from '../lib/components/ui/index';

  const location = useLocation();
  $: state =
    new URLSearchParams($location.search).get('state') === 'completed' ? 'completed' : 'open';

  let completedRows: Followup[] = [];
  let loading = true;
  let error: string | null = null;
  let pendingIds: Set<string> = new Set();

  async function loadOpen(): Promise<void> {
    loading = true;
    error = null;
    try {
      await followupsStore.load();
    } catch (err) {
      error = err instanceof Error ? err.message : $_('pages.followups.load_error');
      toastStore.error($_('pages.followups.load_error'));
    } finally {
      loading = false;
    }
  }

  async function loadCompleted(): Promise<void> {
    loading = true;
    error = null;
    try {
      completedRows = await api.followups.list('completed');
    } catch (err) {
      error = err instanceof Error ? err.message : $_('pages.followups.load_error');
      toastStore.error($_('pages.followups.load_error'));
    } finally {
      loading = false;
    }
  }

  function load(): void {
    if (state === 'completed') loadCompleted();
    else loadOpen();
  }

  // Reactive on `state` (E8: state lives in the URL) — also runs once at mount.
  $: if (state) {
    load();
  }

  $: groups = groupFollowups($followupsStore);

  function firstDocument(f: Followup) {
    return f.documents[0];
  }

  function documentUrl(documentId: string): string {
    return `/documents/${documentId}`;
  }

  function handleTitleClick(e: MouseEvent, documentId: string) {
    if (e.ctrlKey || e.metaKey || e.button === 1) return;
    e.preventDefault();
    navigate(documentUrl(documentId));
  }

  function handleToggleClick(e: MouseEvent, target: 'open' | 'completed') {
    if (e.ctrlKey || e.metaKey || e.button === 1) return;
    e.preventDefault();
    navigate(target === 'completed' ? '/followups?state=completed' : '/followups');
  }

  // F3: complete()/reopen() can fail (409 — someone else already acted on it elsewhere) — the
  // checkbox is a one-way binding (`checked={...}` on a fixed expression), so on failure it must
  // be reverted by hand, otherwise it stays checked/unchecked as if the action had succeeded.
  // F2: the wire call suppresses the global toast (api/followups.ts) — this is the only toast.
  async function undoComplete(id: string): Promise<void> {
    try {
      await followupsStore.reopen(id);
      completedRows = completedRows.filter((r) => r.id !== id);
      toastStore.success($_('pages.followups.reopen_success'));
    } catch (err) {
      const errorCode = (err as { errorCode?: string } | undefined)?.errorCode;
      toastStore.error(followupErrorMessage(errorCode, $_));
    }
  }

  async function handleToggleFollowup(f: Followup, e: Event): Promise<void> {
    const checkbox = e.target as HTMLInputElement;
    const checkedAfterClick = checkbox.checked;
    pendingIds = new Set(pendingIds).add(f.id);
    try {
      if (state === 'completed') {
        await followupsStore.reopen(f.id);
        completedRows = completedRows.filter((r) => r.id !== f.id);
        toastStore.success($_('pages.followups.reopen_success'));
      } else {
        await followupsStore.complete(f.id);
        toastStore.success($_('pages.followups.complete_success'), undefined, {
          action: { label: $_('pages.followups.undo'), onClick: () => undoComplete(f.id) },
        });
      }
    } catch (err) {
      checkbox.checked = !checkedAfterClick;
      const errorCode = (err as { errorCode?: string } | undefined)?.errorCode;
      toastStore.error(followupErrorMessage(errorCode, $_));
    } finally {
      const next = new Set(pendingIds);
      next.delete(f.id);
      pendingIds = next;
    }
  }
</script>

{#snippet thumb(documentId: string)}
  <div
    aria-hidden="true"
    class="shrink-0 w-12 h-16 rounded border border-border bg-bg-alt overflow-hidden"
  >
    <SignedThumbnailImage
      {documentId}
      size="medium"
      className="w-full h-full object-contain"
      lazy
    />
  </div>
{/snippet}

<PageHeader title={$_('pages.followups.title')}>
  <svelte:fragment slot="actions">
    <a
      href="/followups"
      on:click={(e) => handleToggleClick(e, 'open')}
      aria-current={state === 'open' ? 'page' : undefined}
      class="px-3 py-1.5 rounded text-sm font-medium no-underline {state === 'open'
        ? 'bg-accent-subtle text-accent'
        : 'text-text-secondary hover:text-text'}"
    >
      {$_('pages.followups.tab_open')}
    </a>
    <a
      href="/followups?state=completed"
      on:click={(e) => handleToggleClick(e, 'completed')}
      aria-current={state === 'completed' ? 'page' : undefined}
      class="px-3 py-1.5 rounded text-sm font-medium no-underline {state === 'completed'
        ? 'bg-accent-subtle text-accent'
        : 'text-text-secondary hover:text-text'}"
    >
      {$_('pages.followups.tab_completed')}
    </a>
  </svelte:fragment>
</PageHeader>

{#if loading}
  <div data-testid="followups-loading" class="flex justify-center py-12">
    <Spinner size="lg" label={$_('messages.loading')} />
  </div>
{:else if error}
  <div
    data-testid="followups-error"
    class="flex flex-col items-center justify-center py-12 px-4 text-center"
  >
    <p class="text-sm text-text-secondary mb-4">{error}</p>
    <Button data-testid="followups-retry" variant="primary" on:click={load}>
      {$_('pages.error.retry')}
    </Button>
  </div>
{:else if state === 'completed'}
  {#if completedRows.length === 0}
    <div data-testid="followups-empty">
      <EmptyState
        title={$_('pages.followups.empty_completed_title')}
        description={$_('pages.followups.empty_completed_description')}
      />
    </div>
  {:else}
    <div class="bg-surface border border-border rounded-lg divide-y divide-border/50">
      {#each completedRows as f (f.id)}
        {@const doc = firstDocument(f)}
        <div data-testid={`followups-row-${f.id}`} class="flex items-start gap-3 px-4 py-3">
          <div>
            <label class="flex items-center justify-center cursor-pointer p-2 -m-2">
              <input
                type="checkbox"
                data-testid={`followups-checkbox-${f.id}`}
                checked={true}
                disabled={pendingIds.has(f.id)}
                on:change={(e) => handleToggleFollowup(f, e)}
                class="accent-accent cursor-pointer"
                aria-label={$_('pages.followups.mark_reopen_label', {
                  values: { subject: f.subject },
                })}
              />
            </label>
          </div>
          {#if doc}{@render thumb(doc.documentId)}{/if}
          <div class="flex-1 min-w-0">
            <div class="flex flex-wrap items-baseline gap-2">
              {#if f.completedAt}
                <span class="text-sm text-text"
                  >{$_('pages.followups.completed_on', {
                    values: { date: formatDate(f.completedAt, $userSettings) },
                  })}</span
                >
              {/if}
              <span class="text-sm font-medium text-text">{f.subject}</span>
            </div>
            <div class="text-sm mt-0.5 flex items-center gap-2">
              {#if doc?.displayName}
                <a
                  href={documentUrl(doc.documentId)}
                  on:click={(e) => handleTitleClick(e, doc.documentId)}
                  class="text-text-secondary hover:text-accent no-underline truncate"
                >
                  {doc.displayName}
                </a>
                {#if doc.inBin}
                  <Badge>{$_('pages.followups.in_bin_hint')}</Badge>
                {/if}
              {:else}
                <span class="italic text-text-muted">{$_('pages.followups.document_gone')}</span>
              {/if}
            </div>
          </div>
        </div>
      {/each}
    </div>
  {/if}
{:else if $followupsStore.length === 0}
  <div data-testid="followups-empty">
    <EmptyState
      title={$_('pages.followups.empty_title')}
      description={$_('pages.followups.empty_description')}
    />
  </div>
{:else}
  <div class="flex flex-col gap-6">
    {#each [{ key: 'overdue', label: $_('pages.followups.group_overdue'), rows: groups.overdue, testid: 'followups-group-overdue' }, { key: 'this-week', label: $_('pages.followups.group_this_week'), rows: groups.thisWeek, testid: 'followups-group-this-week' }, { key: 'later', label: $_('pages.followups.group_later'), rows: groups.later, testid: 'followups-group-later' }] as group (group.key)}
      {#if group.rows.length > 0}
        <section data-testid={group.testid}>
          <h2 class="text-sm font-semibold text-text-secondary mb-2">
            {group.label} ({group.rows.length})
          </h2>
          <div class="bg-surface border border-border rounded-lg divide-y divide-border/50">
            {#each group.rows as f (f.id)}
              {@const doc = firstDocument(f)}
              {@const overdue = isFollowupOverdue(f.dueDate)}
              <div data-testid={`followups-row-${f.id}`} class="flex items-start gap-3 px-4 py-3">
                <div>
                  <label class="flex items-center justify-center cursor-pointer p-2 -m-2">
                    <input
                      type="checkbox"
                      data-testid={`followups-checkbox-${f.id}`}
                      checked={false}
                      disabled={pendingIds.has(f.id)}
                      on:change={(e) => handleToggleFollowup(f, e)}
                      class="accent-accent cursor-pointer"
                      aria-label={$_('pages.followups.mark_done_label', {
                        values: { subject: f.subject },
                      })}
                    />
                  </label>
                </div>
                {#if doc}{@render thumb(doc.documentId)}{/if}
                <div class="flex-1 min-w-0">
                  <div class="flex flex-wrap items-baseline gap-2">
                    <span class="text-sm {overdue ? 'text-error font-medium' : 'text-text'}">
                      {formatDate(f.dueDate, $userSettings)}
                    </span>
                    <span class="text-sm font-medium text-text">{f.subject}</span>
                  </div>
                  <div class="text-sm mt-0.5 flex items-center gap-2">
                    {#if doc?.displayName}
                      <a
                        href={documentUrl(doc.documentId)}
                        on:click={(e) => handleTitleClick(e, doc.documentId)}
                        class="text-text-secondary hover:text-accent no-underline truncate"
                      >
                        {doc.displayName}
                      </a>
                      {#if doc.inBin}
                        <Badge>{$_('pages.followups.in_bin_hint')}</Badge>
                      {/if}
                    {:else}
                      <span class="italic text-text-muted">
                        {$_('pages.followups.document_gone')}
                      </span>
                    {/if}
                  </div>
                </div>
              </div>
            {/each}
          </div>
        </section>
      {/if}
    {/each}
  </div>
{/if}
